import moment, { Moment } from 'moment';
import PersonData from '@/ts/PersonData';
import MessageData from '@/ts/MessageData';
import { ID } from '@/ts/store/Constants';
import { includes, truncate } from 'lodash';
import store from '@/store';

export default class ConversationData {
    public id: number;
    public authorId: number;
    public persisted: boolean = true;

    public propertyId?: number = null;
    public status?: string = 'open';
    public title?: string | null = null;

    public participants: number[] = [];
    public messages: MessageData[] = [];
    public hasUnseenMessages: boolean = false;
    public newestMessageMoment: moment.Moment;
    public newestMessageTimestamp: number = 0;

    public hidden: boolean = false;
    public external: boolean = false;

    private messageMap: { [index: number]: number } = {};
    private maxLoadedMessageId: number = 0;

    constructor(    
        id: number, 
        participants: number[], 
        messages: MessageData[] = [], 
        authorId: number, 
        propertyId: number, 
        status: string = 'open', 
        title: string | null = null,
        external: boolean,
    ) {
        this.id = id;
        this.participants = (participants !== null) ? participants : [];
        this.messages = messages;
        this.authorId = authorId;
        this.propertyId = propertyId;
        this.status = status;
        this.title = title;
        this.external = external;
    }

    public addParticipant(userId: number): ConversationData {
        let arrayIndex = null;

        if (!includes(this.participants, userId)) {
            arrayIndex = this.participants.push(userId);
        } 

        return this;
    }

    public removeParticipant(userId: number): ConversationData {
        if (includes(this.participants, userId)) {
            this.participants.splice(this.participants.indexOf(userId), 1);            
        } 

        return this;
    }

    public addMessage(
        messageId: number, 
        authorId: number, 
        bySelf: boolean,
        time: string, 
        messageBody: string, 
        unread: boolean,
        attachmentGUID: string | null,
        originalFilename: string | null,
        imageWidth: number | null,
        imageHeight: number | null,
        actionType: string | null,
        actionTarget: number | null,
    ) {
        if (unread === true && this.hasUnseenMessages !== true) {
            this.hasUnseenMessages = true;
        }

        const messageMoment = moment.utc(time);

        let imageMetaData = null;

        if (attachmentGUID && imageWidth && imageHeight) {
            imageMetaData = {
                width: imageWidth,
                height: imageHeight,
            };
        }

        this.messageMap[messageId] = this.messages.push(new MessageData(
            messageId,
            authorId,
            bySelf,
            messageMoment,
            messageBody,
            unread,
            attachmentGUID,
            originalFilename,
            actionType,
            actionTarget,
            imageMetaData,
        )) - 1;

        if (messageId > this.maxLoadedMessageId) {
            this.maxLoadedMessageId = messageId;

            const timestamp = parseInt(messageMoment.format('x'), 10);
             
            if (timestamp > this.newestMessageTimestamp) {
                this.newestMessageMoment = messageMoment;
                this.newestMessageTimestamp = timestamp;
            }
        }
    }

    public containsMessage(messageId: number): boolean {
        return this.messageMap.hasOwnProperty(messageId);
    }

    public getSearchableText(): string {
        let searchString = '';

        for (const participant of this.participants) {
            searchString += this.getUser(participant).fullName;
        }

        for (const message of this.messages) {
            searchString += message.toString();
        }

        return searchString.toLowerCase();
    }

    public addMessageData(message: MessageData): ConversationData {
        this.messages.push(message);
        return this;
    }

    public markAsSeen(): number {
        this.hasUnseenMessages = false;

        let unseenMessageCount = 0;

        for (const message of this.messages) {
            if (!message.bySelf) {
                if (message.unread === true) {
                    message.unread = false;
                    unseenMessageCount++;
                }
            }
        }

        return unseenMessageCount;
    }

    public reset(): void {
        this.persisted = false;
        this.participants = [];
        this.messages = [];
        this.hasUnseenMessages = false;
        this.messageMap = {};
        this.maxLoadedMessageId = 0;
    }

    get hasMessages(): boolean {
        return (
            this.messages !== null
         && this.messages.constructor === Array
         && this.messages.length > 0
         );
    }

    get messageList(): MessageData[] {
        if (this.messages.length > 0) {
            const clone = this.messages.slice(0);

            return clone.sort((firstMessage, secondMessage) => {
                if (firstMessage.sortingCriterion > secondMessage.sortingCriterion) {
                    return 1;
                }
    
                if (firstMessage.sortingCriterion < secondMessage.sortingCriterion) {
                    return -1;
                }
    
                return 0;
            });
        }

        return this.messages;
    }

    get lastMessage(): MessageData | null {
        if (!this.hasMessages) {
            return null;
        }

        for (let iterator = this.messageList.length - 1; iterator >= 0; iterator--) {
            if (!this.messageList[iterator].isAction) {
                return this.messageList[iterator];
            }
        }

        return null;
    }

    get firstInitial(): string {
        if (this.participants.length === 0) {
            return '';
        }

        return this.getUser(this.participants[0]).firstInitial;
    }

    get participantNames(): string {
        if (this.participants.length > 1) {
            return this.participants.map((p) => this.getUser(p).firstName).join(', ');
        } else {
            return this.getUser(this.participants[0]).fullName;
        }
    }

    get recipients(): number[] {
        const userId = store.getters['User/getUserId'];

        return this.participants.filter((participant) => {
            return (participant !== userId);
        });
    }
    
    get recipientNames(): string {
        if (this.participants.length < 2) {
            return '';
        }
    
        return this.recipients.map((participant) => this.getUser(participant).firstName).join(', ');
    }

    get hasMultipleParticipants(): boolean {
        return this.participants.length > 1;
    }

    get time(): string {
        // Moment 'LT' = localized time
        if (!(this.persisted && this.messages.length > 0)) {
            return moment().local().format('LT');
        }

        return (this.newestMessageIsFromToday())
            ? this.newestMessageMoment.local().format('LT')
            : this.newestMessageMoment.local().format('MMM DD');
    }

    get unixTime(): number {
        return parseInt((this.persisted && this.messages.length > 0)
            ? this.newestMessageMoment.format('x')
            : moment().utc().format('x'), 10);
    }

    get emphasized(): boolean {
        return (this.hasUnseenMessages === true);
    }

    get lastMessageId(): number {
        return this.maxLoadedMessageId;
    }
    
    get isUnderComposition() {
        return (this.id === ID.NEW_CONVERSATION);
    }

    public newestMessageIsFromToday(): boolean {
        return this.newestMessageMoment.isSame(moment(), 'd');
    }

    public markAsUnpersisted(): ConversationData {
        this.persisted = false;

        return this;
    }

    public markAsPersisted(): ConversationData {
        this.persisted = true;

        return this;
    }

    public hide(): ConversationData {
        this.hidden = true;

        return this;
    }

    public show(): ConversationData {
        this.hidden = false;

        return this;
    }

    public shortParticipantNames(truncateCharacters: number = null): string {
        if (truncateCharacters !== null) {

            return truncate(this.participantNames, {
                length: truncateCharacters,
            });
        }

        return this.participantNames;
    }

    public hasMessageId(messageId: number): boolean {
        return this.messageMap[messageId] !== undefined;
    }
    
    private getUser(userId: number): PersonData {
        return store.getters['User/getCacheUser'](userId);
    }

}
